import { fetcher } from '@common/utils/fetcher';

import type { ZodiacSign } from '@entities/profile/types';

export interface Referrals {
  referral_link: string;
  registered_users: number;
  premium_reward_hours: number;
}

export interface VerificationStatus {
  is_verified: boolean;
  has_pending_request: boolean;
  latest_request: {
    id: string;
    status: 'pending' | 'approved' | 'rejected';
    created_at: string;
    reviewed_at: string;
    rejection_reason: string | null;
  } | null;
}

export interface User {
  id: string;
  username: string;
  dob: string;
  about: string;
  city: string;
  goal: string;
  looking_for: 0 | 1;
  sex: 0 | 1;
  telegram_user_id: string;
  photos: string[];
  interests: string[];
  age: number;
  is_premium: boolean;
  study_and_work: string[];
  zodiac: ZodiacSign;
  views_on_alcohol?: string;
  smoking_status?: string;
  children?: string;
  height?: number;
  has_match: boolean;
  liked_me: boolean;
  is_verified: boolean;
  sports?: string;
  premium_until?: string;
  notification_preferences: {
    receive_message_notifications: boolean;
    receive_like_notifications: boolean;
    receive_match_notifications: boolean;
  };
  approval_status: {
    status: 'approved' | 'pending' | 'pending_update' | 'rejected';
    reasons?: {
      photo: 'specific_photo_code' | null;
      description: 'rejected' | null;
    };
  };
  verification_status: VerificationStatus;
  referrals: Referrals;
}

export type GetUserByIdParams = {
  userId: string;
};

export type GetUserByIdResponse = User;

export type PostProfileUpdateParams = {
  username?: string;
  dob?: string;
  about?: string;
  city?: string;
  goal?: string;
  photos?: string[];
  interests?: string[];
  study_and_work?: string[];
  views_on_alcohol?: string;
  smoking_status?: string;
  children?: string;
  height?: number;
  sports?: string;
};

export type PostProfileUpdateResponse = User;

export type PostDeleteAccountParams = {
  reason: string;
};

export type PostDeleteAccountResponse = null;

export type PutNotificationPreferencesParams = {
  receive_message_notifications?: boolean;
  receive_like_notifications?: boolean;
  receive_match_notifications?: boolean;
};

export type PutNotificationPreferencesResponse =
  Required<PutNotificationPreferencesParams> & {
    user_id: string;
    created_at: string;
    updated_at: string;
  };

export const getUserById = ({ userId }: GetUserByIdParams) => {
  return fetcher<GetUserByIdResponse>({
    url: `/user/${userId}`,
    method: 'GET',
  });
};

export const postProfileUpdate = (data: PostProfileUpdateParams) => {
  return fetcher<PostProfileUpdateResponse>({
    url: '/profile',
    method: 'POST',
    data,
  });
};

export const postDeleteAccount = (data: PostDeleteAccountParams) => {
  return fetcher<PostDeleteAccountResponse>({
    url: '/delete',
    method: 'POST',
    data,
  });
};

export const putNotificationPreferences = (
  data: PutNotificationPreferencesParams,
) => {
  return fetcher<PutNotificationPreferencesResponse>({
    url: '/notification-preferences',
    method: 'PUT',
    data,
  });
};
